import {
  AdminSpinner,
  Button,
  Div,
  MenuNamesResponse,
  P,
  disconnectBrandProvider,
  disconnectLocationWithProvider,
  getAllLocationsProvidersPaused,
  getAllProvidersPaused,
  getBrandProviderStatuses,
  getBrandsProviderConfigs,
  getLocationProviderStatuses,
  getLocationsProviderConfigs,
  isKitchenHubProviderConnection,
  unpauseAllBrandProviders,
  unpauseAllProviders,
  isAdmin,
  isProd,
  read,
  store,
  reportError,
} from '@vgrubs/components';
import { ProviderAndProviderStatusResponse } from 'components/src/types/api/location.types';
import { BrandProviderConfigEntity } from 'components/src/types/brand.types';
import {
  Provider,
  ProviderConnect,
  ProviderConnectionStatus,
  ProviderStatus,
} from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useManageProvidersSocket from './ManageProvidersSocket';
import { PauseAllProvidersModal } from './PauseAllProviders';
import { UnpauseAllProvidersModal } from './UnpauseAllProviders';
import { ProviderComponent } from './ProviderComponent';
import { useProviderSyncStatus } from '../../store/useProviderSyncStatus';
import { activePauseEnd } from './providerPause';
import { ScheduledPauseCards } from './ScheduledPauseCards';
import { useScheduledPauses } from './useScheduledPauses';

enum Tabs {
  Providers = 'providers',
  POS = 'pos',
}

export async function handleDisconnect(
  pathPrefix: 'locations' | 'brands',
  id: string | number,
  provider: Provider,
  statuses: ProviderAndProviderStatusResponse,
  setStatuses: (value: ProviderAndProviderStatusResponse) => void,
  setLoading: Dispatch<SetStateAction<boolean>>,
  success: null | (() => void)
) {
  const disconnectFunction =
    pathPrefix === 'locations'
      ? disconnectLocationWithProvider
      : disconnectBrandProvider;
  try {
    setLoading(true);
    await disconnectFunction(id, provider);

    store('provider-connect', JSON.stringify(null));

    setStatuses(statuses.filter((item) => item.provider !== provider));

    if (success !== null) {
      success();
    }

    setLoading(false);
  } catch (error) {
    // reportError(error);
    store('provider-connect', JSON.stringify(null));
    setLoading(false);
  }
}

export const ManageProviders = ({
  locationId,
  brandId,
  storeName,
  pathPrefix,
}: {
  locationId: string;
  brandId: string;
  storeName: MenuNamesResponse | null;
  pathPrefix: 'locations' | 'brands';
}) => {
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  // --- MOVED UP: derive scope before store usage ---
  const isLocations = pathPrefix === 'locations';
  const id = isLocations ? locationId : brandId;

  // --- REPLACED: scoped selector + scoped setter wrapper ---
  const statuses = useProviderSyncStatus(
    (st) =>
      st.statusesByScope?.find(
        (e) =>
          e.locationId === (isLocations ? Number(id) : 0) &&
          e.brandId === (!isLocations ? Number(id) : 0)
      )?.statuses ?? []
  );

  const setStatuses = (value: ProviderAndProviderStatusResponse) =>
    useProviderSyncStatus.getState().setStatusesFor(
      {
        locationId: isLocations ? Number(id) : 0,
        brandId: !isLocations ? Number(id) : 0,
      },
      value
    );

  const [pauseAllModal, setPauseAllModal] = useState(false);
  const [unpauseAllModal, setUnpauseAllModal] = useState(false);
  // Source of truth for pausing: when true, everything is paused (show
  // "Unpause All"); when false we fall back to the individual provider
  // statuses. Works for both locations and brands.
  const [allPaused, setAllPaused] = useState(false);
  const [providerCredentials, setProviderCredentials] = useState<
    BrandProviderConfigEntity[]
  >([]);

  const providerConnectNode = 'provider-connect';
  const setProviderConnect = (provider: ProviderConnect | null) => {
    store(providerConnectNode, JSON.stringify(provider));
  };

  useManageProvidersSocket({
    brandId: isLocations ? undefined : Number(brandId),
    locationId: isLocations ? Number(locationId) : undefined,
    getProvidersStatuses,
  });

  const [searchParams] = useSearchParams();
  const connectedProvider = searchParams.get('cp');

  const getStatusesFunction =
    pathPrefix === 'locations'
      ? getLocationProviderStatuses
      : getBrandProviderStatuses;

  const getConfigs =
    pathPrefix === 'locations'
      ? getLocationsProviderConfigs
      : getBrandsProviderConfigs;

  async function getProvidersStatuses(
    silent?: boolean,
    buttonLoading?: boolean
  ) {
    try {
      if (!silent) setLoading(true);
      if (buttonLoading) setButtonLoading(true);
      // Add cache-busting to prevent browser from serving cached responses
      const statusesData = await getStatusesFunction(id, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
        params: {
          _t: Date.now(), // Timestamp to bypass cache
        },
      });

      const providerConnect: ProviderConnect | null = read(providerConnectNode)
        ? JSON.parse(read(providerConnectNode) as string)
        : null;

      setStatuses(statusesData.data.data);

      const isWaitingForMenu = statusesData.data.data.some(
        (item) =>
          item.provider === Provider.grubhubPos &&
          item.connectionStatus === ProviderConnectionStatus.waitingMenu
      );

      if (
        providerConnect &&
        isKitchenHubProviderConnection(providerConnect.provider) &&
        id === providerConnect.id &&
        pathPrefix === providerConnect.pathPrefix &&
        !connectedProvider &&
        !isWaitingForMenu
      ) {
        handleDisconnect(
          providerConnect.pathPrefix,
          providerConnect.id,
          providerConnect.provider,
          statuses,
          setStatuses,
          setLoading,
          null
        );
      } else {
        setLoading(false);
        setProviderConnect(null);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
      setButtonLoading(false);
    }
  }

  async function getProvidersCredentials() {
    try {
      setLoading2(true);
      const { data } = await getConfigs(id);
      setProviderCredentials(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading2(false);
    }
  }

  // paused-all is the source of truth for the "everything paused" state.
  async function getAllPausedState() {
    try {
      const { data } = isLocations
        ? await getAllLocationsProvidersPaused(id)
        : await getAllProvidersPaused(id);
      setAllPaused(data.data.paused);
    } catch (error) {
      reportError(error);
    }
  }

  async function handleUnpauseAll() {
    try {
      setLoading(true);
      await (isLocations
        ? unpauseAllProviders(id)
        : unpauseAllBrandProviders(id));
      setAllPaused(false);
      await getProvidersStatuses(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getProvidersStatuses();
    getProvidersCredentials();
    getAllPausedState();

    // keep your cleanup — now it clears only the scoped bucket (via wrapper)
    return () => {
      setStatuses([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A pause ends on the platforms with no event to tell us, so the list is
  // fetched again just after the soonest end it knows about.
  const nextPauseEnd = useMemo(() => {
    const ends = statuses
      .map((s) => activePauseEnd(s.pausedUntil)?.toMillis())
      .filter((end): end is number => end !== undefined);
    return ends.length ? Math.min(...ends) : null;
  }, [statuses]);

  useEffect(() => {
    if (nextPauseEnd === null) return;
    const timer = setTimeout(() => {
      getProvidersStatuses(true);
      getAllPausedState();
    }, Math.max(nextPauseEnd - Date.now(), 0) + 5000);
    return () => clearTimeout(timer);
  }, [nextPauseEnd]);

  const timeZone = storeName?.location?.timeZone;

  // The location's own brand is sent as null, like the location itself.
  const scheduled = useScheduledPauses({
    locationId,
    brandId: isLocations || storeName?.brand?.isLocation ? null : Number(id),
    onStarted: () => {
      getProvidersStatuses(true);
      getAllPausedState();
    },
  });
  const scheduledAll = scheduled.of(null);

  const hasOnline = useMemo(
    () => statuses.some((item) => item.status === ProviderStatus.online),
    [statuses]
  );

  // On admin, for a location, "Pause All" stays available even when no
  // provider reads as online. Partner/brandOwner and the brand scope keep
  // the original behaviour (only shown when something is online).
  const canPauseAll = hasOnline || (isAdmin && isLocations);

  //TODO check this with dimi
  function removeProviders(objects: Provider[]) {
    // The rest of the filtering logic stays the same
    const hasGloriafoodConnected = statuses.some(
      (obj) =>
        obj?.provider === Provider.gloriafood &&
        obj?.connectionStatus === ProviderConnectionStatus.connected
    );

    const hasLocalForYouConnected = statuses.some(
      (obj) =>
        obj?.provider === Provider.localForYou &&
        obj?.connectionStatus === ProviderConnectionStatus.connected
    );

    if (hasGloriafoodConnected) {
      return objects.filter((obj) => obj !== 'localForYou');
    } else if (hasLocalForYouConnected) {
      return objects.filter((obj) => obj !== 'gloriafood');
    } else {
      return objects; // Return the original array if no matching conditions
    }
  }

  // Delivery.com is no longer offered. A store the backend still reports it
  // for keeps the row, so it can be paused or disconnected.
  const hasDeliveryCom = statuses.some(
    (s) => s?.provider === Provider.delivery
  );

  const filteredProviders = Object.values(Provider)
    .filter((p) => !isProd || p !== Provider.vGrubsAI)
    .filter((p) => p !== Provider.delivery || hasDeliveryCom);

  return (
    <>
      {(loading || loading2) && <AdminSpinner />}

      {removeProviders(filteredProviders).map((p, i) => {
        const rawStatus = statuses.find((s) => s.provider === p);
        // When everything is paused (source of truth), every connected
        // provider must read as paused regardless of its individual status.
        const status =
          rawStatus && allPaused
            ? { ...rawStatus, status: ProviderStatus.offline }
            : rawStatus;
        const credentials = providerCredentials?.find((s) => s.provider === p);
        const syncStatus = statuses.find((s) => s.provider === p)?.syncStatus;
        return (
          <ProviderComponent
            key={i}
            provider={p}
            id={id}
            status={status}
            pathPrefix={pathPrefix}
            setStatuses={setStatuses}
            statuses={statuses}
            credentials={credentials}
            setProviderConnect={setProviderConnect}
            getProvidersStatuses={getProvidersStatuses}
            location={storeName?.location || null}
            brand={storeName?.brand || null}
            syncStatus={syncStatus || null}
            buttonLoading={buttonLoading}
            timeZone={timeZone}
            scheduled={scheduled}
          />
        );
      })}

      {/* Out here too: while everything is paused the Pause All dialog is
          out of reach, and a pause set for later still has to be cancellable. */}
      {scheduledAll.length > 0 && (
        <Div $flex $justifyEnd $centerSM $width="100%" $mt="32">
          <Div $maxWidth="300px" $maxWidthSM="100%" $width="100%">
            <ScheduledPauseCards
              pauses={scheduledAll}
              timeZone={timeZone}
              onCancel={scheduled.cancel}
            />
          </Div>
        </Div>
      )}

      {/* paused-all is the source of truth: when everything is paused show
          "Unpause All"; otherwise fall back to the individual statuses and
          offer "Pause All" if anything is online. */}
      {allPaused && (
        <Div $flex $justifyEnd $centerSM $width="100%" $mt="32">
          <Div
            $flex
            $justifyEnd
            $centerSM
            $maxWidth="300px"
            $maxWidthSM="100%"
            $width="100%"
          >
            <Button
              $w="100%"
              $selected
              onClick={() => setUnpauseAllModal(true)}
            >
              Unpause All Providers
            </Button>
          </Div>
        </Div>
      )}

      {!allPaused && canPauseAll && (
        <>
          <P $m="32px 0 20px 0" $right $centerSM>
            Pausing all providers includes pausing virtual brand providers if
            there are any.{` `}
          </P>
          <Div $flex $justifyEnd $centerSM $width="100%">
            <Div
              $flex
              $justifyEnd
              $centerSM
              $maxWidth="300px"
              $maxWidthSM="100%"
              $width="100%"
            >
              <Button
                $w="100%"
                $selected
                $gray
                onClick={() => setPauseAllModal(true)}
              >
                Pause All Providers
              </Button>
            </Div>
          </Div>
        </>
      )}

      <PauseAllProvidersModal
        modalIsOpen={pauseAllModal}
        closeModal={() => {
          setPauseAllModal(false);
        }}
        setLoading={setLoading}
        id={id}
        isLocations={isLocations}
        setStatuses={setStatuses}
        statuses={statuses}
        timeZone={timeZone}
        scheduled={scheduled}
        onPausedAll={() => {
          setAllPaused(true);
          // For the pausedUntil of every row.
          getProvidersStatuses(true);
        }}
      />

      <UnpauseAllProvidersModal
        modalIsOpen={unpauseAllModal}
        closeModal={() => setUnpauseAllModal(false)}
        onConfirm={handleUnpauseAll}
      />
    </>
  );
};
